import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { PlayerAvatar } from "./PlayerAvatar";
import { api } from "../lib/api";
import { computeRadar, winRate } from "./helpers";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Loader2, Swords } from "lucide-react";

const COLOR_A = "#10B981";
const COLOR_B = "#06B6D4";

function mergeProgression(pa = [], pb = []) {
  const days = Array.from(new Set([...pa, ...pb].map((x) => x.matchday))).sort((a, b) => a - b);
  const mapA = Object.fromEntries(pa.map((x) => [x.matchday, x.cumulative]));
  const mapB = Object.fromEntries(pb.map((x) => [x.matchday, x.cumulative]));
  let lastA = 0, lastB = 0;
  return days.map((d) => {
    if (mapA[d] !== undefined) lastA = mapA[d];
    if (mapB[d] !== undefined) lastB = mapB[d];
    return { label: `G${d}`, A: lastA, B: lastB };
  });
}

function CompareRow({ label, a, b, better }) {
  const aWins = better === "a";
  const bWins = better === "b";
  return (
    <div className="grid grid-cols-3 items-center text-center py-2 border-b border-white/5">
      <span className={`font-mono font-bold ${aWins ? "text-emerald-400" : "text-slate-300"}`}>{a}</span>
      <span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`font-mono font-bold ${bWins ? "text-cyan-400" : "text-slate-300"}`}>{b}</span>
    </div>
  );
}

export function ComparePlayersDialog({ open, onOpenChange, options }) {
  const [idA, setIdA] = useState(options[0]?.id || "");
  const [idB, setIdB] = useState(options[1]?.id || "");
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [h2h, setH2h] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !idA || !idB) return;
    setLoading(true);
    Promise.all([
      api.get(`/players/${idA}/stats`),
      api.get(`/players/${idB}/stats`),
      api.get(`/head-to-head/${idA}/${idB}`),
    ])
      .then(([a, b, h]) => { setDataA(a.data); setDataB(b.data); setH2h(h.data); })
      .finally(() => setLoading(false));
  }, [open, idA, idB]);

  const sA = dataA?.stats;
  const sB = dataB?.stats;

  const radarData = useMemo(() => {
    const ra = computeRadar(sA);
    const rb = computeRadar(sB);
    return ra.map((row, i) => ({ axis: row.axis, A: row.v, B: rb[i]?.v || 0 }));
  }, [sA, sB]);

  const progression = useMemo(
    () => mergeProgression(dataA?.progression, dataB?.progression),
    [dataA, dataB]
  );

  const h2hTrend = useMemo(() => {
    if (!h2h) return [];
    let ca = 0, cb = 0;
    return h2h.encounters.map((e) => {
      if (e.winner === "a") ca += 1;
      else cb += 1;
      return { label: `G${e.matchday}`, A: ca, B: cb };
    });
  }, [h2h]);

  const cmp = (av, bv, invert = false) => {
    if (av === bv) return null;
    const aBetter = invert ? av < bv : av > bv;
    return aBetter ? "a" : "b";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-900/95 backdrop-blur-xl border border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="compare-players-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Swords className="w-5 h-5 text-emerald-400" />
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">
              Confronto Giocatori
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 pt-1">
          <Select value={idA} onValueChange={setIdA}>
            <SelectTrigger
              data-testid="compare-select-a"
              className="bg-slate-900 border-slate-700 focus:ring-emerald-500"
              style={{ borderColor: COLOR_A + "66" }}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-72">
              {options.map((o) => (
                <SelectItem key={o.id} value={o.id} disabled={o.id === idB}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={idB} onValueChange={setIdB}>
            <SelectTrigger
              data-testid="compare-select-b"
              className="bg-slate-900 border-slate-700 focus:ring-cyan-500"
              style={{ borderColor: COLOR_B + "66" }}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-72">
              {options.map((o) => (
                <SelectItem key={o.id} value={o.id} disabled={o.id === idA}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading || !sA || !sB ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : (
          <div className="space-y-5 pt-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <PlayerAvatar id={dataA.player.id} name={dataA.player.name} size={40} />
                <div className="min-w-0">
                  <div className="font-bold text-white truncate" style={{ color: COLOR_A }}>{dataA.player.name}</div>
                  <div className="font-mono text-[11px] text-slate-500">#{sA.position}</div>
                </div>
              </div>
              <span className="font-display font-black text-slate-600 text-lg">VS</span>
              <div className="flex items-center gap-2 min-w-0 flex-row-reverse text-right">
                <PlayerAvatar id={dataB.player.id} name={dataB.player.name} size={40} />
                <div className="min-w-0">
                  <div className="font-bold text-white truncate" style={{ color: COLOR_B }}>{dataB.player.name}</div>
                  <div className="font-mono text-[11px] text-slate-500">#{sB.position}</div>
                </div>
              </div>
            </div>

            <div className="ap-glass rounded-2xl p-3 h-64">
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <RadarChart data={radarData} outerRadius="72%">
                  <PolarGrid stroke="#334155" />
                  <PolarAngleAxis dataKey="axis" tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 600 }} />
                  <Radar name={dataA.player.name} dataKey="A" stroke={COLOR_A} fill={COLOR_A} fillOpacity={0.35} />
                  <Radar name={dataB.player.name} dataKey="B" stroke={COLOR_B} fill={COLOR_B} fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <div className="ap-glass rounded-2xl px-4 py-2" data-testid="compare-table">
              <CompareRow label="Punti" a={sA.points} b={sB.points} better={cmp(sA.points, sB.points)} />
              <CompareRow label="Vittorie" a={sA.wins} b={sB.wins} better={cmp(sA.wins, sB.wins)} />
              <CompareRow label="Win %" a={`${winRate(sA)}%`} b={`${winRate(sB)}%`} better={cmp(winRate(sA), winRate(sB))} />
              <CompareRow label="Diff games" a={(sA.games_diff > 0 ? "+" : "") + sA.games_diff} b={(sB.games_diff > 0 ? "+" : "") + sB.games_diff} better={cmp(sA.games_diff, sB.games_diff)} />
              <CompareRow label="Games subiti" a={sA.games_lost} b={sB.games_lost} better={cmp(sA.games_lost, sB.games_lost, true)} />
            </div>

            {h2h && (
              <div className="ap-glass rounded-2xl p-4" data-testid="head-to-head">
                <div className="text-center text-[11px] uppercase tracking-widest text-slate-500 mb-3">
                  Testa a Testa Diretto
                </div>
                {h2h.played === 0 ? (
                  <p className="text-center text-sm text-slate-500">
                    I due non si sono mai affrontati come avversari.
                  </p>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-6">
                      <div className="text-center">
                        <div className="font-mono font-black text-4xl" style={{ color: COLOR_A }}>
                          {h2h.a_wins}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate max-w-[110px]">{h2h.a}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-display font-black text-slate-500 text-lg">
                          {h2h.played}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-600">sfide</div>
                      </div>
                      <div className="text-center">
                        <div className="font-mono font-black text-4xl" style={{ color: COLOR_B }}>
                          {h2h.b_wins}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate max-w-[110px]">{h2h.b}</div>
                      </div>
                    </div>

                    {h2h.played >= 2 && (
                      <div className="mt-4 h-44" data-testid="h2h-trend">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 text-center">
                          Andamento scontri diretti
                        </div>
                        <ResponsiveContainer width="100%" height="100%" minHeight={130}>
                          <LineChart data={h2hTrend} margin={{ top: 6, right: 12, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                            <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 10 }} />
                            <Line type="monotone" dataKey="A" name={h2h.a} stroke={COLOR_A} strokeWidth={2.5} dot={{ r: 3 }} />
                            <Line type="monotone" dataKey="B" name={h2h.b} stroke={COLOR_B} strokeWidth={2.5} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    <div className="mt-3 space-y-1.5">
                      {h2h.encounters.map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-slate-950/50"
                        >
                          <span className="font-mono text-slate-500">G{e.matchday} · C{e.court}</span>
                          <span className="text-slate-400 truncate px-2">
                            {e.a_partner} <span className="text-slate-600">vs</span> {e.b_partner}
                          </span>
                          <span
                            className="font-mono font-bold"
                            style={{ color: e.winner === "a" ? COLOR_A : COLOR_B }}
                          >
                            {e.score}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {progression.length > 0 && (
              <div className="ap-glass rounded-2xl p-3 h-56">
                <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                  <LineChart data={progression} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 10 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="A" name={dataA.player.name} stroke={COLOR_A} strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="B" name={dataB.player.name} stroke={COLOR_B} strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
